// ─────────────────────────────────────────────────────────
// lib/env.ts — Runtime environment validation
//
// Checks required env vars at startup and exposes helpers.
// Keeps every env reference in one place — no scattered
// `import.meta.env.VITE_*` across modules.
// ─────────────────────────────────────────────────────────

function get(key: string): string {
  return (import.meta.env[key] as string | undefined) ?? ''
}

// ── Raw env values ─────────────────────────────────────────
export const ENV = {
  SUPABASE_URL:          get('VITE_SUPABASE_URL'),
  SUPABASE_ANON_KEY:     get('VITE_SUPABASE_ANON_KEY'),
  ENCRYPTION_PASSPHRASE: get('VITE_ENCRYPTION_PASSPHRASE'),
  USE_SUPABASE_DB:       get('VITE_USE_SUPABASE_DB'),
  USE_SUPABASE_STORAGE:  get('VITE_USE_SUPABASE_STORAGE'),
  SENTRY_DSN:            get('VITE_SENTRY_DSN'),
} as const

// ── Validation ─────────────────────────────────────────────
const PLACEHOLDER_URL = 'https://placeholder.supabase.co'
const PLACEHOLDER_KEY = 'placeholder-anon-key'

/** True when real Supabase credentials are present */
export const hasSupabaseConfig: boolean =
  !!ENV.SUPABASE_URL &&
  ENV.SUPABASE_URL !== PLACEHOLDER_URL &&
  ENV.SUPABASE_URL.startsWith('https://') &&
  ENV.SUPABASE_URL.includes('.supabase.co') &&
  !!ENV.SUPABASE_ANON_KEY &&
  ENV.SUPABASE_ANON_KEY !== PLACEHOLDER_KEY &&
  ENV.SUPABASE_ANON_KEY.length > 20

/** True when encryption passphrase meets minimum length */
export const hasEncryptionKey: boolean =
  ENV.ENCRYPTION_PASSPHRASE.length >= 16

/** Validation report — shown in SetupBanner */
export interface EnvReport {
  valid:    boolean
  warnings: string[]
  errors:   string[]
}

export function validateEnv(): EnvReport {
  const errors:   string[] = []
  const warnings: string[] = []

  if (!ENV.SUPABASE_URL || ENV.SUPABASE_URL === PLACEHOLDER_URL) {
    errors.push('VITE_SUPABASE_URL is not set — copy .env.local.example to .env.local and fill in your Supabase project URL')
  } else if (!ENV.SUPABASE_URL.startsWith('https://') || !ENV.SUPABASE_URL.includes('.supabase.co')) {
    errors.push('VITE_SUPABASE_URL looks incorrect — it should be https://your-project-ref.supabase.co')
  }

  if (!ENV.SUPABASE_ANON_KEY || ENV.SUPABASE_ANON_KEY === PLACEHOLDER_KEY) {
    errors.push('VITE_SUPABASE_ANON_KEY is not set — add your Supabase anon/public key to .env.local')
  }

  if (!hasEncryptionKey) {
    warnings.push('VITE_ENCRYPTION_PASSPHRASE is not set — data will be stored unencrypted (acceptable for local dev only)')
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  }
}

// ── Dev-mode reporter ──────────────────────────────────────
if (import.meta.env.DEV) {
  const report = validateEnv()
  if (report.errors.length) {
    console.error('[CivilBillDesk] ❌ ENV errors:\n' + report.errors.map(e => `  • ${e}`).join('\n'))
  }
  if (report.warnings.length) {
    console.warn('[CivilBillDesk] ⚠ ENV warnings:\n' + report.warnings.map(w => `  • ${w}`).join('\n'))
  }
  if (report.valid) {
    if (import.meta.env.DEV) console.info(`[CivilBillDesk] ✅ Supabase configured: ${ENV.SUPABASE_URL}`)
  }
}
